<template>
  <span
    class="swiper-parallax-fancy-text"
    :data-fancy-text="fancyTextConfig"
  >
    <span
      :key="props.introOnce ? 'anim-root' : props.text"
      ref="animRoot"
      class="anime-text words chars splitting"
      :class="[
        `effect-${props.effect}`,
        { 'chars-delays-ready': delaysReady },
        { 'chars-settled': props.introOnce && introComplete },
        { 'is-measuring': isMeasuring }
      ]"
      :style="containerStyle"
    >
      <template v-for="(word, wordIndex) in words" :key="wordIndex">
        <span
          class="word"
          :data-word="word"
          :style="{ '--word-index': wordIndex }"
        >
          <template v-for="(char, charIndex) in wordChars[wordIndex]" :key="`${wordIndex}-${charIndex}`">
            <span
              class="char"
              :data-char="char"
              :style="getCharStyle(wordIndex, charIndex)"
            >{{ char }}</span>
          </template>
        </span>
        <span v-if="wordIndex < words.length - 1" class="whitespace" />
      </template>
    </span>
  </span>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

const introComplete = ref(false)
let introTimer: ReturnType<typeof setTimeout> | undefined
let resizeDebounce: ReturnType<typeof setTimeout> | undefined

interface Props {
  text: string
  effect?: 'rotate' | 'fade' | 'slide'
  delay?: number
  duration?: number
  /** Після першої анімації зміни тексту (i18n) без remount і без повтору keyframes */
  introOnce?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  effect: 'rotate',
  delay: 50,
  duration: 600,
  introOnce: true
})

const animRoot = ref<HTMLElement | null>(null)
const isMeasuring = ref(false)
const delaysReady = ref(false)
/** Затримка старту анімації кожної літери (мс), порядок як у DOM; порожньо до першого виміру */
const charDelaysMs = ref<number[]>([])

const splitIntoChars = (str: string): string[] => Array.from(str)

const words = computed(() => props.text.split(/\s+/u).filter(Boolean))

const wordChars = computed(() => words.value.map(word => splitIntoChars(word)))

const fancyTextConfig = computed(() =>
  JSON.stringify({ effect: props.effect, string: [props.text] })
)

const totalChars = computed(() =>
  wordChars.value.reduce((sum, chars) => sum + chars.length, 0)
)

const containerStyle = computed(() => ({
  '--word-total': words.value.length,
  '--char-total': totalChars.value,
  '--stagger-ms': `${props.delay}ms`,
  '--char-duration-ms': `${props.duration}ms`
}))

const getGlobalCharIndex = (wordIndex: number, charIndex: number) => {
  let globalCharIndex = 0
  for (let i = 0; i < wordIndex; i++) {
    const chars = wordChars.value[i]
    if (chars) globalCharIndex += chars.length
  }
  return globalCharIndex + charIndex
}

/** Затримки по візуальних рядках: наступний рядок після завершення останньої літери попереднього */
function buildLineStaggerDelays (elements: HTMLElement[], stagger: number, duration: number): number[] {
  if (!elements.length) return []
  const tops = elements.map(el => el.getBoundingClientRect().top)
  const lineGroups: number[][] = []
  let line: number[] = [0]
  let lineTop = tops[0]!
  const rowTol = 6
  for (let i = 1; i < elements.length; i++) {
    const t = tops[i]!
    if (Math.abs(t - lineTop) < rowTol) {
      line.push(i)
    } else {
      lineGroups.push(line)
      line = [i]
      lineTop = t
    }
  }
  lineGroups.push(line)

  const delays = new Array<number>(elements.length).fill(0)
  let lineBase = 0
  for (const indices of lineGroups) {
    const n = indices.length
    for (let k = 0; k < indices.length; k++) {
      const domIdx = indices[k]!
      delays[domIdx] = lineBase + k * stagger
    }
    lineBase += (n - 1) * stagger + duration
  }
  return delays
}

function delaysEqual (a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false
  }
  return true
}

function measureCharDelays (afterLayout?: () => void) {
  nextTick(() => {
    isMeasuring.value = true
    nextTick(() => {
      requestAnimationFrame(() => {
        const root = animRoot.value
        if (!root) {
          isMeasuring.value = false
          afterLayout?.()
          return
        }
        const chars = [...root.querySelectorAll<HTMLElement>('.char')]
        if (chars.length !== totalChars.value || totalChars.value === 0) {
          isMeasuring.value = false
          afterLayout?.()
          return
        }
        const next = buildLineStaggerDelays(chars, props.delay, props.duration)
        if (!delaysEqual(charDelaysMs.value, next)) {
          charDelaysMs.value = next
        }
        isMeasuring.value = false
        delaysReady.value = true
        afterLayout?.()
      })
    })
  })
}

function getCharStyle (wordIndex: number, charIndex: number) {
  const idx = getGlobalCharIndex(wordIndex, charIndex)
  if (!delaysReady.value) return { '--anim-delay': '0ms' }
  const measured = charDelaysMs.value[idx] ?? 0
  return { '--anim-delay': `${measured}ms` }
}

/** Оцінка тривалості intro до виміру рядків (один довгий рядок) */
const introDurationFallbackMs = computed(() => {
  const n = Math.max(0, totalChars.value - 1)
  return props.delay * n + props.duration + 80
})

function scheduleIntroComplete () {
  if (!props.introOnce) return
  if (introTimer !== undefined) {
    clearTimeout(introTimer)
    introTimer = undefined
  }
  if (import.meta.client && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
    introComplete.value = true
    return
  }
  const delays = charDelaysMs.value
  const ms = delays.length === totalChars.value && totalChars.value > 0
    ? Math.max(...delays) + props.duration + 80
    : introDurationFallbackMs.value
  introTimer = setTimeout(() => {
    introComplete.value = true
    introTimer = undefined
  }, ms)
}

watch(() => props.text, () => {
  delaysReady.value = false
  charDelaysMs.value = []
  if (props.introOnce) introComplete.value = false
  measureCharDelays(() => {
    if (props.introOnce) scheduleIntroComplete()
  })
})

watch(
  animRoot,
  (el, _prev, onCleanup) => {
    if (!import.meta.client || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => {
      if (resizeDebounce !== undefined) clearTimeout(resizeDebounce)
      resizeDebounce = setTimeout(() => {
        resizeDebounce = undefined
        if (props.introOnce && !introComplete.value) return
        measureCharDelays()
      }, 120)
    })
    if (el) ro.observe(el)
    onCleanup(() => {
      ro.disconnect()
      if (resizeDebounce !== undefined) clearTimeout(resizeDebounce)
    })
  },
  { immediate: true }
)

onMounted(() => {
  measureCharDelays(() => {
    if (props.introOnce) scheduleIntroComplete()
  })
})

onBeforeUnmount(() => {
  if (introTimer !== undefined) clearTimeout(introTimer)
  if (resizeDebounce !== undefined) clearTimeout(resizeDebounce)
})
</script>

<style scoped>
.swiper-parallax-fancy-text {
  display: inline-block;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
  color: inherit;
  perspective: 1200px;
  transform-style: preserve-3d;
}

.anime-text {
  display: inline-block;
  transform-style: preserve-3d;
}

.anime-text.words.chars.splitting {
  letter-spacing: 1.5px;
}

.word {
  display: inline-block;
}

.char {
  display: inline-block;
  transform-origin: center center;
  backface-visibility: hidden;
}

.whitespace {
  display: inline-block;
  width: 0.25em;
}

@keyframes char-rotate-in {
  from {
    opacity: 0;
    transform: rotateX(90deg);
  }
  to {
    opacity: 1;
    transform: rotateX(0deg);
  }
}

@keyframes char-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes char-slide-in {
  from {
    opacity: 0;
    transform: translateY(0.35em);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.effect-rotate:not(.chars-delays-ready):not(.is-measuring):not(.chars-settled) .char {
  animation: none;
  opacity: 0;
  transform: rotateX(90deg);
}

.effect-fade:not(.chars-delays-ready):not(.is-measuring):not(.chars-settled) .char {
  animation: none;
  opacity: 0;
}

.effect-slide:not(.chars-delays-ready):not(.is-measuring):not(.chars-settled) .char {
  animation: none;
  opacity: 0;
  transform: translateY(0.35em);
}

.effect-rotate.chars-delays-ready:not(.is-measuring):not(.chars-settled) .char {
  animation: char-rotate-in var(--char-duration-ms, 600ms) ease-out both;
  animation-delay: var(--anim-delay, 0ms);
}

.effect-fade.chars-delays-ready:not(.is-measuring):not(.chars-settled) .char {
  animation: char-fade-in var(--char-duration-ms, 600ms) ease-out both;
  animation-delay: var(--anim-delay, 0ms);
}

.effect-slide.chars-delays-ready:not(.is-measuring):not(.chars-settled) .char {
  animation: char-slide-in var(--char-duration-ms, 600ms) ease-out both;
  animation-delay: var(--anim-delay, 0ms);
}

.chars-settled .char {
  animation: none !important;
  opacity: 1;
  transform: none;
}

/* Вимір без rotateX: інакше getBoundingClientRect().top різний → хибні «рядки» й діри в тексті */
.anime-text.is-measuring {
  opacity: 0;
  pointer-events: none;
}

.anime-text.is-measuring .char {
  animation: none !important;
  opacity: 1 !important;
  transform: none !important;
}

@media (prefers-reduced-motion: reduce) {
  .effect-rotate .char,
  .effect-fade .char,
  .effect-slide .char {
    animation: none;
    opacity: 1;
    transform: none;
  }
}
</style>
